import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Provider, createStore } from '@iris-platform/incontext';
import {
  always,
  complement,
  compose,
  curry,
  filter,
  includes,
  isNil,
  lensPath,
  lensProp,
  map,
  mapObjIndexed,
  otherwise,
  path,
  prop,
  propSatisfies,
  reduce,
  set,
  split,
  then,
  when,
  zip,
  __,
} from 'ramda';
import Tooltip from '@material-ui/core/Tooltip';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import { withApollo } from 'react-apollo';
import { Settings as SettingsIcon } from '../../icons';
import { KeySpaceManager } from './containers';
import { getDomains, getKeys, getBasicAuthKeys } from './queries';
import reducer from './state';
import { Key } from './models';
import { withBaseStyles } from './styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';

const insertKeyAtPath = curry((useBasicAuth, keySpace, path) => set(lensPath(path), Key.from({ path, basicAuth: useBasicAuth }), keySpace));

const buildKeySpaceFromKeys = curry((useBasicAuth, keys) => {
  if (keys instanceof Error) {
    console.log(keys);
    return {};
  }
  const keyPaths = map(split('/'), keys);

  return reduce(insertKeyAtPath(useBasicAuth), {}, keyPaths);
});

const loadKeySpaces = curry(async (client, domains) => {
  const getKeysWithClient = getKeys(client);
  const getKeySpaceWithClient = compose(
    then(buildKeySpaceFromKeys(false)),
    otherwise(when(propSatisfies(includes('unauthorized'), 'message'), always([]))),
    getKeysWithClient,
  );

  const queries = map(domain => ({ domain }), domains);
  const promisedKeySpaces = map(getKeySpaceWithClient, queries);
  const keySpaces = await Promise.all(promisedKeySpaces);
  const filteredKeyspaces = filter(complement(isNil), keySpaces);

  const domainLenses = map(lensProp, domains);
  const keySpaceAssignments = zip(domainLenses, filteredKeyspaces);

  return reduce(
    (accum, [domainLense, keySpace]) => set(domainLense, keySpace, accum),
    {},
    keySpaceAssignments,
  );
});

const loadBasicAuthKeySpaces = curry(async (client, tokens) => {
  const getKeysWithClient = getBasicAuthKeys(client);
  const getKeySpaceWithClient = compose(
    then(buildKeySpaceFromKeys(true)),
    otherwise(when(propSatisfies(includes('unauthorized'), 'message'), always([]))),
    getKeysWithClient,
  );

  const queries = map(token => ({ token }), tokens);
  const promisedKeySpaces = map(getKeySpaceWithClient, queries);
  const keySpaces = await Promise.all(promisedKeySpaces);
  const filteredKeyspaces = filter(complement(isNil), keySpaces);

  const tokenLenses = map(lensProp, tokens);
  const keySpaceAssignments = zip(tokenLenses, filteredKeyspaces);

  return reduce(
    (accum, [tokenLense, keySpace]) => set(tokenLense, keySpace, accum),
    {},
    keySpaceAssignments,
  );
});

const initializeStore = (keySpace, useBasicAuth) => {
  return compose(
    keySpaceManagers => createStore(reducer, { keySpaceManagers }),
    mapObjIndexed((keySpace) => ({ keySpace, newKey: Key.from({basicAuth: useBasicAuth}) })),
  )(keySpace);
}

class KeyManager extends Component {
  LOADING = 'LOADING'

  ERROR = 'ERROR'

  LOADED = 'LOADED'

  state = {
    tabIndex: 0,
    retries: 0,
    status: this.LOADING,
    keySpaces: null,
    error: null,
    basicAuthToken: "",
  }

  constructor(props) {
    super(props);
    this.getWaitDuration = this.getWaitDuration.bind(this);
    this.fetchKeySpaces = this.fetchKeySpaces.bind(this);
  }

  componentDidMount() {
    this.fetchKeySpaces();
  }

  getWaitDuration() {
    const { delayMultiplier } = this.props;
    const { retries } = this.state;
    const unboundedDuration = (retries ** 2) * delayMultiplier;
    const boundedDuration = Math.min(unboundedDuration, 20 * 1000);

    return boundedDuration;
  }

  async fetchDomainKeySpaces() {
    this.setState({ status: this.LOADING });
    const { client } = this.props;
    const { retries, tabIndex } = this.state;
    const useBasicAuth = tabIndex == 1;

    try {
      const domains = await getDomains(client, {});
      const keySpacesByDomain = await loadKeySpaces(client, domains, useBasicAuth);
      const store = initializeStore(keySpacesByDomain, false);
      this.setState({
        store,
        status: this.LOADED,
        retries: 0,
      });
    } catch (error) {
      const userFrendlyError = new Error('Unable to load keys');
      this.setState(
        { status: this.ERROR, error: userFrendlyError, retries: retries + 1 },
        () => setTimeout(this.fetchKeySpaces, this.getWaitDuration()),
      );
    }
  }

  async fetchBasicAuthKeySpaces() {
    this.setState({ status: this.LOADING });
    const { client } = this.props;
    const { retries, basicAuthToken } = this.state;
    try {
      const tokens = (basicAuthToken !== "") ? [basicAuthToken] : [];
      const keySpacesByToken = await loadBasicAuthKeySpaces(client, tokens);
      const store = initializeStore(keySpacesByToken, true);
      this.setState({
        store,
        status: this.LOADED,
        retries: 0,
      });
    } catch (error) {
      const userFrendlyError = new Error('Unable to load keys');
      this.setState(
        { status: this.ERROR, error: userFrendlyError, retries: retries + 1 },
        () => setTimeout(this.fetchKeySpaces, this.getWaitDuration()),
      );
    }
  }

  fetchKeySpaces() {
    const { tabIndex } = this.state;
    const useBasicAuth = tabIndex == 1;

    if (useBasicAuth) {
      this.fetchBasicAuthKeySpaces();
    } else {
      this.fetchDomainKeySpaces();
    }
  }

  handleTabChange = (event, value) => {
    this.setState({
      ...this.state,
      tabIndex: value,
    }, () => {
      this.fetchKeySpaces();
    });
  };

  handleBasicAuthTokenChange(e) {
    this.setState({ basicAuthToken: e.target.value });
  }

  renderKeySpaces() {
    const getState = path(__, this.state);
    const getProp = prop(__, this.props);
    const getClass = prop(__, getProp('classes'));
    const useBasicAuth = this.state.tabIndex == 1;

    const basicAuthControls = useBasicAuth ? (
      <div className={getClass('basicAuthControlsContainer')}>
        <TextField
          id="standard-textarea"
          placeholder="Basic auth token"
          className={getClass('basicAuthTokenText')}
          onChange={this.handleBasicAuthTokenChange.bind(this)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          fullWidth={true}
        />
        <Button
          variant="contained"
          color="primary"
          color="default"
          className={getClass('basicAuthLoadButton')}
          onClick={() => {
            this.fetchKeySpaces();
          }}
        >
          Load
        </Button>
      </div>
    ) : null;

    switch (getState(['status'])) {
      case this.ERROR:
        return (
          <div className={getClass('fullWidthRow')}>
            <span className={getClass('domainHeading')}>Failed to load keys</span>
            <Tooltip title={getState(['error', 'message'])}>
              <ErrorIcon className={getClass('errorIcon')} />
            </Tooltip>
          </div>
        );
      case this.LOADING:
        return (
          <div className={getClass('fullWidthRow')}>
            <span className={getClass('domainHeading')}>Loading...</span>
            <SettingsIcon className={`${getClass('settingsIcon')} loading`} />
          </div>
        );
      default:
        return (
          <Provider store={getState(['store'])}>
            {basicAuthControls}
            <KeySpaceManager useBasicAuth={useBasicAuth} />
          </Provider>
        );
    }
  }

  render() {
    const { tabIndex } = this.state;
    const { role } = this.props;
    
    return (
      <React.Fragment>
        <Tabs
          value={tabIndex}
          onChange={this.handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Domains" />
          <Tab label="Basic Auth" />
        </Tabs>
        {this.renderKeySpaces()}
      </React.Fragment>
    );
    
  }
}

KeyManager.propTypes = {
  delayMultiplier: PropTypes.number.isRequired,
};

KeyManager.defaultProps = {
  delayMultiplier: 1000,
};

export default withApollo(withBaseStyles(KeyManager));
